
import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';

type Theme = 'light' | 'dark' | 'slate';

export function useTheme() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [theme, setTheme] = useState<Theme>('dark');
  const [isLoading, setIsLoading] = useState(true);

  // Mutation to update theme on server
  const updateThemeMutation = useMutation({
    mutationFn: async (newTheme: Theme) => {
      return apiRequest('PATCH', '/api/profile', { theme: newTheme });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
    },
  });

  useEffect(() => {
    const initializeTheme = () => {
      if (authLoading) {
        return; // Wait for auth to load
      }

      if (user?.theme) {
        // User is authenticated and has a theme preference
        setTheme(user.theme as Theme);
      } else if (!user) {
        // User is not authenticated, use localStorage for guest theme
        const guestTheme = localStorage.getItem('guest-theme') as Theme;
        if (guestTheme) {
          setTheme(guestTheme);
        } else {
          setTheme('dark');
          localStorage.setItem('guest-theme', 'dark');
        }
      } else {
        // User is authenticated but has no theme set, default to dark
        setTheme('dark');
      }
      
      setIsLoading(false);
    };

    initializeTheme();
  }, [user?.theme, authLoading]);

  useEffect(() => {
    // Apply theme to document immediately
    const root = document.documentElement;
    
    // Remove all theme classes first
    root.classList.remove('light', 'dark', 'slate');
    
    // Add the current theme class
    root.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    // Cycle through themes: light -> slate -> dark -> light
    let newTheme: Theme;
    if (theme === 'light') {
      newTheme = 'slate';
    } else if (theme === 'slate') {
      newTheme = 'dark';
    } else {
      newTheme = 'light';
    }
    
    setTheme(newTheme);
    
    if (user) {
      // Save to server for authenticated users
      updateThemeMutation.mutate(newTheme);
    } else {
      // Save to localStorage for guests
      localStorage.setItem('guest-theme', newTheme);
    }
  };

  const setSpecificTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    
    if (user) {
      // Save to server for authenticated users
      updateThemeMutation.mutate(newTheme);
    } else {
      // Save to localStorage for guests
      localStorage.setItem('guest-theme', newTheme);
    }
  };

  return {
    theme,
    toggleTheme,
    setTheme: setSpecificTheme,
    isLoading: isLoading || authLoading
  };
}
